import {
	Button,
	MuiAlert,
	MuiRadioGroup,
	Paper,
	ResponsiveDrawer,
	Spin,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	dateToIsoDateOnly,
	strIsNullOrEmpty,
	useRenderer
} from '@fox/atoms';
import { CreditCardId, DirectDebitId, PAYMENT_SELECTOR_PREFIX, PaymentMethod, PaymentSelectorProps } from '@fox/finance';
import { FlexColumnSpaced, FlexRowSpaced, Padded, SpaceBetweenRow } from '@fox/layout';
import { Extension, sendUpdate, useSubscribeTopicWithHandler } from 'fox-extensions';
import moment from 'moment';
import { nanoid } from 'nanoid';
import React, { useState } from 'react';
import { useContentContext } from '../../../content-context';
import { useFinancialContext } from '../../../financial-config-context';
import { FINANCIAL_DATA_FETCHED, REFETCH_FINANCIAL, REFETCH_INVOICES, REFETCH_RECEIPTS } from '../../../topics';
import { useMakePaymentMutation, useUpdateSubscriptionPaymentMethodMutation } from '../../mutation-hooks/mutation-implementations';
import { LineItem, SwiftFoxPlansResult, SwiftFoxSubscription } from '../contracts';
import {
	ReceiptStatusChip,
	TABLE_BUTTON_STYLE,
	convertMemberPortalCapabilityToSfEnumValue,
	convertMemberPortalToSfMethodEnumValue,
	formatCurrency,
	renderDate
} from '../shared';

export function filterApplicableMethod(methodRequirements: SwiftFoxPlansResult['requirements'], method: PaymentMethod): boolean {
	return (methodRequirements.length === 0 || methodRequirements?.some(r => r.values.includes(method))) ?? false;
}

export type WebCreditCard = Omit<CreditCardId, 'creditCardId'> & { creditCardId: string };
export type WebDirectDebit = Omit<DirectDebitId, 'directDebitId'> & { directDebitId: string };
export type WebPaymentType = WebCreditCard | WebDirectDebit;

function RenewSubscription({
	plan,
	closeDrawer,
	useLinkedOrganisationData,
	allowedProviders,
	payableLineItems,
	currency,
	setNeedsRefetch,
	subscription
}: {
	plan: SwiftFoxPlansResult;
	closeDrawer: () => void;
	useLinkedOrganisationData: boolean;
	payableLineItems: LineItem[];
	currency: string;
	setNeedsRefetch: (needsRefetch: boolean) => void;
	allowedProviders?: string[];
	subscription: SwiftFoxSubscription;
}) {
	const { contentId, requestToken } = useContentContext();
	const { providers, customerError, customerLoading, getCustomers } = useFinancialContext();
	const customerData = getCustomers(useLinkedOrganisationData);
	const render = useRenderer();
	useSubscribeTopicWithHandler(FINANCIAL_DATA_FETCHED, () => render());
	const [selectedPayment, setSelectedPayment] = useState<WebPaymentType>(
		subscription.creditCardId
			? {
					creditCardId: subscription.creditCardId,
					method: PaymentMethod.CreditCard,
					provider: subscription.provider
			  }
			: subscription.directDebitId
			? {
					directDebitId: subscription.directDebitId,
					method: PaymentMethod.DirectDebit,
					provider: subscription.provider
			  }
			: undefined
	);

	const {
		loading: paymentLoading,
		mutate: makePayment,
		error: paymentError,
		data: paymentResult
	} = useMakePaymentMutation({
		requestToken,
		topics: [REFETCH_RECEIPTS, REFETCH_INVOICES]
	});

	const {
		error: updateSubscriptionPaymentMethodError,
		loading: updateSubscriptionPaymentMethodLoading,
		mutate: updateSubscriptionPaymentMethod
	} = useUpdateSubscriptionPaymentMethodMutation({ requestToken, topics: [REFETCH_FINANCIAL] });

	const transactionRef = moment().format('YYYYMMDD-HHmmss-') + nanoid(5);

	if (!strIsNullOrEmpty(customerError)) return <MuiAlert severity="error">{customerError}</MuiAlert>;

	const availableProviders = providers.filter(
		p =>
			(!plan.requirements ||
				filterApplicableMethod(
					plan.requirements.filter(r => r.type === 'subscription' && r.fieldId === 'method'),
					p.method
				)) &&
			(allowedProviders?.length ? allowedProviders.includes(p.provider.toLowerCase()) : true)
	);

	const totalAmount = payableLineItems.reduce((prev, cur) => {
		prev = prev + cur.amount;
		return prev;
	}, 0);

	const numberOfPaymentMethods = customerData.flatMap(c => c.creditCards).length + customerData.flatMap(c => c.directDebits).length;
	const hasPaymentMethods = numberOfPaymentMethods > 0;

	if (!hasPaymentMethods)
		return (
			<MuiAlert severity="warning">
				You do not have any payment methods available, please add a payment method and try again.
			</MuiAlert>
		);

	if (!paymentError && paymentResult?.id) {
		return (
			<Padded>
				<FlexColumnSpaced>
					<SpaceBetweenRow>
						<Typography style={{ fontWeight: 'bold' }}>Receipt number:</Typography>
						<Typography>{paymentResult.receiptNo}</Typography>
					</SpaceBetweenRow>
					<SpaceBetweenRow>
						<Typography style={{ fontWeight: 'bold' }}>Transaction reference</Typography>
						<Typography>
							<Typography>{paymentResult.transactionRef}</Typography>
						</Typography>
					</SpaceBetweenRow>
					<SpaceBetweenRow>
						<Typography style={{ fontWeight: 'bold' }}>Date: </Typography>
						<Typography>{renderDate(paymentResult.createdOn)}</Typography>
					</SpaceBetweenRow>
					<SpaceBetweenRow>
						<Typography style={{ fontWeight: 'bold' }}>Status</Typography>
						<ReceiptStatusChip receipt={paymentResult} />
					</SpaceBetweenRow>
					{paymentResult?.success ? (
						<MuiAlert severity="success">Payment complete</MuiAlert>
					) : (
						<MuiAlert severity="error">Payment failed</MuiAlert>
					)}
				</FlexColumnSpaced>
			</Padded>
		);
	}

	return (
		<Padded>
			<FlexColumnSpaced>
				<Typography style={{ fontWeight: 'bold' }}>Renew</Typography>
				{payableLineItems.length > 0 ? (
					<TableContainer component={Paper}>
						<Table aria-label="failed-receipts-table">
							<TableHead>
								<TableRow>
									<TableCell>Comment</TableCell>
									<TableCell align="right">Type</TableCell>
									<TableCell align="right">Amount</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{payableLineItems.map((payableLineItem, index) => {
									return (
										<TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
											<TableCell style={{ maxWidth: '10rem' }}>{payableLineItem.comment}</TableCell>
											<TableCell align="right">{payableLineItem.lineType.name}</TableCell>
											<TableCell align="right">{formatCurrency(payableLineItem.amount, currency)}</TableCell>
										</TableRow>
									);
								})}
								<TableRow>
									<TableCell style={{ fontWeight: 'bold' }}>Total</TableCell>
									<TableCell style={{ fontWeight: 'bold' }} align="right">
										{formatCurrency(totalAmount, currency)}
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</TableContainer>
				) : null}
				<Spin spinning={customerLoading}>
					{availableProviders?.length > 0 ? (
						<MuiRadioGroup
							value={JSON.stringify(selectedPayment) ?? null}
							name="payment"
							onChange={(_, value) => {
								const newValue = JSON.parse(value);
								setSelectedPayment(
									newValue.creditCardId
										? {
												creditCardId: newValue.creditCardId,
												method: newValue.method,
												provider: newValue.provider
										  }
										: newValue.directDebitId
										? {
												directDebitId: newValue.directDebitId,
												method: newValue.method,
												provider: newValue.provider
										  }
										: undefined
								);
							}}
						>
							{availableProviders.map((c, i) => {
								const provider = {
									...c,
									capabilities: c.capabilities.map(capability => convertMemberPortalCapabilityToSfEnumValue(capability))
								};
								return (
									<React.Fragment key={`${c.provider}${c.method}`}>
										<Extension<PaymentSelectorProps<WebPaymentType>>
											name={`${PAYMENT_SELECTOR_PREFIX}.${convertMemberPortalToSfMethodEnumValue(c.method)}`}
											props={{
												customers: customerData,
												provider,
												selectedPayment,
												setSelectedPayment,
												meta: {
													useLinkedOrganisationData: useLinkedOrganisationData
												}
											}}
										/>
									</React.Fragment>
								);
							})}
						</MuiRadioGroup>
					) : (
						<MuiAlert severity="info">No payment providers available</MuiAlert>
					)}
				</Spin>
				<Typography>
					Total amount you will be charged: <span style={{ fontWeight: 'bold' }}>{formatCurrency(totalAmount, currency)}</span>
				</Typography>

				<FlexRowSpaced style={{ justifyContent: 'flex-end' }}>
					<Button
						disabled={paymentLoading || updateSubscriptionPaymentMethodLoading}
						style={{ maxWidth: '100px' }}
						onClick={() => closeDrawer()}
						variant="tertiary"
						color="error"
					>
						Cancel
					</Button>
					<Button
						disabled={!selectedPayment || !!paymentResult?.id || !!paymentError} //just to make sure
						loading={paymentLoading || updateSubscriptionPaymentMethodLoading}
						onClick={async () => {
							const result = await makePayment({
								contentId,
								currency,
								effectiveDate: dateToIsoDateOnly(new Date()),
								lines: payableLineItems.map(o => ({
									amount: o.amount,
									comment: o.comment,
									lineTypeId: o.lineTypeId,
									subscriptionId: o.subscriptionId
								})),
								...selectedPayment,
								receiptNo: transactionRef,
								transactionRef: transactionRef,
								useLinkedOrganisationData,
								subscriptionDescription: plan.name
							});
							setNeedsRefetch(true);
							//if payment was successful and payment method has changed, update the sub
							//?? undefined, just to make sure we dont consider to/from null to/from undefined as a change
							const hasPaymentMethodChanged =
								(selectedPayment?.['creditCardId'] ?? undefined) !== (subscription.creditCardId ?? undefined) ||
								(selectedPayment?.['directDebitId'] ?? undefined) !== (subscription.directDebitId ?? undefined);
							if (hasPaymentMethodChanged && result?.success) {
								await updateSubscriptionPaymentMethod({
									contentId,
									id: subscription.id,
									method: selectedPayment?.method,
									provider: selectedPayment?.provider,
									creditCardId: selectedPayment?.['creditCardId'],
									directDebitId: selectedPayment?.['directDebitId']
								});
							}
						}}
						sx={{ maxWidth: '100px' }}
						variant="primary"
					>
						Renew
					</Button>
				</FlexRowSpaced>
				{!strIsNullOrEmpty(updateSubscriptionPaymentMethodError) ? (
					<MuiAlert severity="error">{updateSubscriptionPaymentMethodError}</MuiAlert>
				) : null}
				{!strIsNullOrEmpty(paymentError) ? <MuiAlert severity="error">{paymentError}</MuiAlert> : null}
				{paymentResult?.id ? <MuiAlert severity="success">Payment complete</MuiAlert> : null}
			</FlexColumnSpaced>
		</Padded>
	);
}

export function RenewSubscriptionDeskTop({
	record,
	plans,
	useLinkedOrganisationData,
	allowedProviders
}: {
	record: SwiftFoxSubscription;
	plans: SwiftFoxPlansResult[];
	useLinkedOrganisationData: boolean;
	allowedProviders?: string[];
}) {
	const { getPayableLineItemsForSubscription, payableLineItemsError: error, payableLineItemsLoading: loading } = useFinancialContext();
	const [open, setOpen] = useState<boolean>(false);

	const data = getPayableLineItemsForSubscription(record.id, useLinkedOrganisationData);
	const [needsRefetch, setNeedsRefetch] = useState<boolean>(false);
	if (error) {
		return <MuiAlert severity="error">{error}</MuiAlert>;
	}
	if (!data?.length || loading) {
		return <></>;
	}
	const plan = plans?.find(p => p.id === record.fee.planId);
	const currency = record.fee.currency ?? 'AUD';

	return (
		<Spin spinning={loading}>
			<Button variant="primary" sx={TABLE_BUTTON_STYLE} onClick={() => setOpen(true)}>
				Renew
			</Button>
			<ResponsiveDrawer
				headerVariant="h5"
				placement="right"
				title="Renew subscription"
				width={500}
				open={open}
				destroyOnClose
				onClose={() => {
					setOpen(false);
					if (needsRefetch) {
						sendUpdate(REFETCH_FINANCIAL);
					}
				}}
			>
				<RenewSubscription
					closeDrawer={() => {
						setOpen(false);
						if (needsRefetch) {
							sendUpdate(REFETCH_FINANCIAL);
						}
					}}
					setNeedsRefetch={setNeedsRefetch}
					plan={plan}
					currency={currency}
					useLinkedOrganisationData={useLinkedOrganisationData}
					allowedProviders={allowedProviders}
					payableLineItems={data}
					subscription={record}
				/>
			</ResponsiveDrawer>
		</Spin>
	);
}

export function RenewSubscriptionMobile({
	record,
	plans,
	useLinkedOrganisationData,
	allowedProviders
}: {
	record: SwiftFoxSubscription;
	plans: SwiftFoxPlansResult[];
	useLinkedOrganisationData: boolean;
	allowedProviders?: string[];
}) {
	const { getPayableLineItemsForSubscription, payableLineItemsError: error, payableLineItemsLoading: loading } = useFinancialContext();
	const [open, setOpen] = useState<boolean>(false);

	const data = getPayableLineItemsForSubscription(record.id, useLinkedOrganisationData);
	const [needsRefetch, setNeedsRefetch] = useState<boolean>(false);
	if (error) {
		return <MuiAlert severity="error">{error}</MuiAlert>;
	}
	if (!data?.length || loading) {
		return <></>;
	}
	const plan = plans?.find(p => p.id === record.fee.planId);
	const currency = record.fee.currency ?? 'AUD';
	return (
		<Spin spinning={loading}>
			<Button variant="primary" sx={TABLE_BUTTON_STYLE} onClick={() => setOpen(true)}>
				Renew
			</Button>
			<ResponsiveDrawer
				headerVariant="h5"
				placement="bottom"
				title="Renew subscription"
				width="100%"
				height="100%"
				style={{ height: '100%' }}
				destroyOnClose
				open={open}
				onClose={() => {
					setOpen(false);
					if (needsRefetch) {
						sendUpdate(REFETCH_FINANCIAL);
					}
				}}
			>
				<RenewSubscription
					closeDrawer={() => {
						setOpen(false);
						if (needsRefetch) {
							sendUpdate(REFETCH_FINANCIAL);
						}
					}}
					setNeedsRefetch={setNeedsRefetch}
					plan={plan}
					currency={currency}
					useLinkedOrganisationData={useLinkedOrganisationData}
					allowedProviders={allowedProviders}
					payableLineItems={data}
					subscription={record}
				/>
			</ResponsiveDrawer>
		</Spin>
	);
}
